import React, { useState } from "react";
import {
  Box,
  Drawer,
  Typography,
  List,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder"; // updated icon
import RenameFolderDialog from "./RenameFolderDialog";
import DeleteFolderDialog from "./DeleteFolderDialog";
import { handleRenameFolder } from "../../../../shared/services/folderUtils";
import FolderDrawerGroup from "./FolderDrawerGroup";
import FolderDrawerListItem from "./FolderDrawerListItem";

import { downloadFolderAsZip } from "../services/downloadFolder";
import { deleteFolder } from "../services/deleteFolder";

function FolderDrawer({
  folders,
  foldersLoading,
  foldersError,
  filesError,
  selectedFolder,
  onAddFolder,
  onFolderSelect,
  refetchFolders,
}) {
  const [openGroups, setOpenGroups] = useState({
    normal: true,
    pending: true,
    approved: true,
    rejected: true,
  });

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuFolder, setMenuFolder] = useState(null);

  //Renaming Files
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [folderToRename, setFolderToRename] = useState(null);
  const [renameError, setRenameError] = useState(null);

  //Deleting Files
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);

  const toggleGroup = (group) => {
    setOpenGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const handleMenuOpen = (event, folder) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuFolder(folder);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuFolder(null);
  };

  const handleDownload = async () => {
    if (!menuFolder) {
      handleMenuClose();
      return;
    }
    try {
      await downloadFolderAsZip(
        menuFolder,
        menuFolder.userId,
        menuFolder.folderId
      );
    } catch (error) {
      console.error("Download failed:", error);
      // Optionally, show user feedback here
    }
    handleMenuClose();
  };

  const handleRename = () => {
    setRenameError(null);
    setFolderToRename(menuFolder);
    setRenameDialogOpen(true);
    handleMenuClose();
  };

  const handleRenameClose = () => {
    setRenameDialogOpen(false);
    setFolderToRename(null);
    setRenameError(null);
  };

  const handleDelete = () => {
    setFolderToDelete(menuFolder);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFolderToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!folderToDelete) return;

    try {
      await deleteFolder(folderToDelete);
      setDeleteDialogOpen(false);
      setFolderToDelete(null);
      if (typeof onFolderSelect === "function") {
        onFolderSelect(null);
      }
      if (typeof refetchFolders === "function") {
        refetchFolders();
      }
    } catch (error) {
      console.error("Failed to delete folder:", error);
      // Optionally show user feedback here
    }
  };

  const handleRenameSubmit = async (newName) => {
    if (!folderToRename) {
      return;
    }
    try {
      await handleRenameFolder({
        userId: folderToRename.userId,
        folderId: folderToRename.folderId,
        newFolderName: newName,
      });
      setRenameDialogOpen(false);
      setFolderToRename(null);
      setRenameError(null);
      // Refresh folders after rename
      if (typeof onFolderSelect === "function") {
        onFolderSelect(null); // Deselect folder to force refresh
      }
      if (typeof refetchFolders === "function") {
        refetchFolders(); // Invalidate and refetch folders
      }
    } catch (error) {
      setRenameError(error.message || "Failed to rename folder.");
      throw error;
    }
  };

  function groupFoldersByStatus(folders) {
    const groups = {
      normal: [],
      pending: [],
      approved: [],
      rejected: [],
    };
    folders.forEach((folder) => {
      const status = folder.status || "draft";
      if (groups[status]) {
        groups[status].push(folder);
      } else {
        groups.normal.push(folder);
      }
    });
    return groups;
  }

  const groupedFolders = groupFoldersByStatus(folders);

  return (
    <>
      <Drawer
        variant="permanent"
        open
        sx={{
          width: 300,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 300,
            boxSizing: "border-box",
            bgcolor: "#f9f9f9",
            borderRight: "1px solid #ddd",
            p: 0,
            display: "flex",
            flexDirection: "column",
            height: "100vh",
          },
        }}
      >
        <Box
          sx={{
            marginTop: 9,
            bgcolor: "#fff",
            borderBottom: "1px solid #ddd",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between", // left label, right button
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              fontSize: 21,
              color: "#333",
              letterSpacing: 0.2,
            }}
          >
            Folders
          </Typography>
          <IconButton
            onClick={onAddFolder}
            color="rgba(0, 0, 0, 0.54)"
            size="small"
            aria-label="Add Folder"
            sx={{ ml: 1 }}
          >
            <CreateNewFolderIcon />
          </IconButton>
        </Box>

        <Box sx={{ overflowY: "auto", flexGrow: 1 }}>
          {foldersLoading && !folders.length ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <Typography variant="body2" color="textSecondary">
                Loading folders...
              </Typography>
            </Box>
          ) : (
            <List component="nav" disablePadding>
              {Object.entries(groupedFolders).map(([status, folders]) => (
                <FolderDrawerGroup
                  key={status}
                  status={status}
                  folders={folders}
                  open={openGroups[status]}
                  toggleGroup={toggleGroup}
                  selectedFolder={selectedFolder}
                  onFolderSelect={onFolderSelect}
                  onMenuOpen={handleMenuOpen}
                  FolderListItem={FolderDrawerListItem}
                />
              ))}
            </List>
          )}
          {(foldersError || filesError) && (
            <Box sx={{ p: 2 }}>
              <Typography color="error" variant="body2">
                {foldersError || filesError}
              </Typography>
            </Box>
          )}
        </Box>

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleDownload}>Download</MenuItem>
          <MenuItem
            onClick={handleRename}
            disabled={
              menuFolder?.status === "pending" ||
              menuFolder?.status === "approved"
            }
          >
            Rename
          </MenuItem>
          <MenuItem
            onClick={handleDelete}
            disabled={menuFolder?.status === "pending"}
            sx={{ color: "red" }}
          >
            Delete
          </MenuItem>
        </Menu>
      </Drawer>
      <RenameFolderDialog
        open={renameDialogOpen}
        onClose={handleRenameClose}
        onSubmit={handleRenameSubmit}
        existingFolderNames={folders.map((f) => f.folderName)}
        initialName={folderToRename ? folderToRename.folderName : ""}
      />
      <DeleteFolderDialog
        open={deleteDialogOpen}
        folderName={folderToDelete ? folderToDelete.folderName : ""}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}

export default FolderDrawer;
